import { 
  AlertTriangle, Ban, Fence, Car, Skull,
  OctagonAlert, DiamondMinus, Signpost, TrainTrack, Waves, Mountain, Droplets, Info,
  Tent, Caravan, Bed, Home,
  UtensilsCrossed, Coffee, Hamburger, ShoppingCart, Store, Beer, Wine, Croissant,
  Mountain as Summit, TreePine, Binoculars, Bike, WavesLadder,
  Hospital, Toilet, ShowerHead, ParkingCircle, Fuel as ServiceStation, Mail, Wrench,
  Bus, TrainFront, Ship, Plane,
  BriefcaseMedical, Flag, Settings,
  Building2, Landmark, School, Tractor, WifiOff, Play, Trophy
} from 'lucide-react'

export interface POIType {
  id: string
  name: string
  icon: any
  category: string
}

export interface POICategory {
  id: string
  name: string
  color: string
  types: POIType[]
}

export const POI_CATEGORIES: POICategory[] = [
  {
    id: 'road-info',
    name: 'Road Information',
    color: '#dc2626', // red-600
    types: [
      { id: 'hazard', name: 'Hazard', icon: AlertTriangle, category: 'road-info' },
      { id: 'no-entry', name: 'No Entry', icon: Ban, category: 'road-info' },
      { id: 'gate', name: 'Gate', icon: Fence, category: 'road-info' },
      { id: 'heavy-traffic', name: 'Heavy Traffic', icon: Car, category: 'road-info' },
      { id: 'danger', name: 'Danger', icon: Skull, category: 'road-info' }
    ]
  },
  {
    id: 'trail-info',
    name: 'Trail Information',
    color: '#eb4d4b', // red
    types: [
      { id: 'warning', name: 'Warning', icon: OctagonAlert, category: 'trail-info' },
      { id: 'single-track', name: 'Single Track', icon: DiamondMinus, category: 'trail-info' },
      { id: 'trailhead', name: 'Trailhead', icon: Signpost, category: 'trail-info' },
      { id: 'rail-trail', name: 'Rail Trail', icon: TrainTrack, category: 'trail-info' },
      { id: 'water-crossing', name: 'Water Crossing', icon: Waves, category: 'trail-info' },
      { id: 'remote-area', name: 'Remote Area', icon: WifiOff, category: 'trail-info' },
      { id: 'info', name: 'Information', icon: Info, category: 'trail-info' }
    ]
  },
  {
    id: 'drinking-water',
    name: 'Drinking Water',
    color: '#0ea5e9', // sky-400
    types: [
      { id: 'drinking-water', name: 'Drinking Water', icon: Droplets, category: 'drinking-water' }
    ]
  },
  {
    id: 'accommodation',
    name: 'Accommodation',
    color: '#4f46e5', // indigo-600
    types: [
      { id: 'campsite', name: 'Campsite', icon: Tent, category: 'accommodation' },
      { id: 'caravan-park', name: 'Caravan Park', icon: Caravan, category: 'accommodation' },
      { id: 'accommodation', name: 'Accommodation', icon: Bed, category: 'accommodation' },
      { id: 'hut', name: 'Hut', icon: Home, category: 'accommodation' }
    ]
  },
  {
    id: 'food-drink',
    name: 'Food & Drink',
    color: '#ea580c', // orange-600
    types: [
      { id: 'food', name: 'Food', icon: UtensilsCrossed, category: 'food-drink' },
      { id: 'cafe', name: 'Cafe', icon: Coffee, category: 'food-drink' },
      { id: 'bakery', name: 'Bakery', icon: Croissant, category: 'food-drink' },
      { id: 'roadhouse', name: 'Roadhouse', icon: Hamburger, category: 'food-drink' },
      { id: 'supermarket', name: 'Supermarket', icon: ShoppingCart, category: 'food-drink' },
      { id: 'general-store', name: 'General Store', icon: Store, category: 'food-drink' },
      { id: 'pub', name: 'Pub', icon: Beer, category: 'food-drink' },
      { id: 'winery', name: 'Winery', icon: Wine, category: 'food-drink' }
    ]
  },
  {
    id: 'scenic',
    name: 'Scenic',
    color: '#059669', // emerald-600
    types: [
      { id: 'summit', name: 'Summit', icon: Summit, category: 'scenic' },
      { id: 'park', name: 'Park', icon: TreePine, category: 'scenic' },
      { id: 'viewpoint', name: 'Viewpoint', icon: Binoculars, category: 'scenic' },
      { id: 'mtb-park', name: 'MTB Park', icon: Bike, category: 'scenic' },
      { id: 'swimming-spot', name: 'Swimming Spot', icon: WavesLadder, category: 'scenic' }
    ]
  },
  {
    id: 'town-services',
    name: 'Town Services',
    color: '#0284c7', // sky-700
    types: [
      { id: 'hospital', name: 'Hospital', icon: Hospital, category: 'town-services' },
      { id: 'toilets', name: 'Toilets', icon: Toilet, category: 'town-services' },
      { id: 'shower', name: 'Shower', icon: ShowerHead, category: 'town-services' },
      { id: 'parking', name: 'Parking', icon: ParkingCircle, category: 'town-services' },
      { id: 'service-station', name: 'Service Station', icon: ServiceStation, category: 'town-services' },
      { id: 'post-office', name: 'Post Office', icon: Mail, category: 'town-services' },
      { id: 'bike-shop', name: 'Bike Shop', icon: Wrench, category: 'town-services' }
    ]
  },
  {
    id: 'transportation',
    name: 'Transportation',
    color: '#14b8a6', // teal-500
    types: [
      { id: 'bus', name: 'Bus', icon: Bus, category: 'transportation' },
      { id: 'train', name: 'Train', icon: TrainFront, category: 'transportation' },
      { id: 'ferry', name: 'Ferry', icon: Ship, category: 'transportation' },
      { id: 'plane', name: 'Plane', icon: Plane, category: 'transportation' }
    ]
  },
  {
    id: 'event-info',
    name: 'Event Information',
    color: '#fb7185', // rose-300
    types: [
      { id: 'aid-station', name: 'Aid Station', icon: BriefcaseMedical, category: 'event-info' },
      { id: 'checkpoint', name: 'Check Point', icon: Flag, category: 'event-info' },
      { id: 'bike-hub', name: 'Bike Hub', icon: Settings, category: 'event-info' }
    ]
  },
  {
    id: 'race-start',
    name: 'Start',
    color: '#22c55e', // green-500
    types: [
      { id: 'start', name: 'Start', icon: Play, category: 'race-start' }
    ]
  },
  {
    id: 'race-finish',
    name: 'Finish',
    color: '#ef4444', // red-500
    types: [
      { id: 'finish', name: 'Finish', icon: Trophy, category: 'race-finish' }
    ]
  },
  {
    id: 'city-town',
    name: 'City / Town',
    color: '#273c75', // dark blue
    types: [
      { id: 'city', name: 'City', icon: Building2, category: 'city-town' },
      { id: 'big-town', name: 'Big Town', icon: Landmark, category: 'city-town' },
      { id: 'mid-town', name: 'Mid-sized Town', icon: School, category: 'city-town' },
      { id: 'small-town', name: 'Small Town', icon: Tractor, category: 'city-town' }
    ]
  }
]

export const getAllPOITypes = (): POIType[] => {
  return POI_CATEGORIES.flatMap(category => category.types)
}

export const getPOITypeById = (id: string): POIType | undefined => {
  return getAllPOITypes().find(type => type.id === id)
}

export const getPOICategoryById = (id: string): POICategory | undefined => {
  return POI_CATEGORIES.find(category => category.id === id)
}